import { useAppTheme } from "@hooks/useAppTheme";
import type { Choice } from "@memorang/types/src/session/new-session";
import { Box } from "@memorang/ui";
import { Pressable, View } from "react-native";
import { Icon, Text } from "react-native-paper";
import useSessionStore from "../../../../hooks/useSessionStore";

interface Props {
	choices: Choice[];
	isAnswered: boolean;
	selectedChoiceIds: string[];
	correctAnswerIds?: string[];
	itemId: number;
	onSelect: (id: string) => void;
}

const deriveFrameworkLabel = (text: string | undefined): string => {
	if (!text) {
		return "Selected framework";
	}
	const upper = text.toUpperCase();
	if (upper.includes("REGULATORY")) {
		return "Influenced More by Regulatory Framework";
	}
	if (upper.includes("DISPOSITIONAL")) {
		return "Influenced More by Dispositional Framework";
	}
	return text;
};

const getChoiceMeta = (text: string | undefined) => {
	const upper = text?.toUpperCase() ?? "";
	if (upper.includes("REGULATORY")) {
		return {
			icon: "scale-balance",
			label: "Influenced More by Regulatory Framework ",
			supportingText:
				"The personal and professional attitudes, values, and beliefs that inform a professional educator’s decision making.",
		};
	}
	if (upper.includes("DISPOSITIONAL")) {
		return {
			icon: "account-heart-outline",
			label: "Influenced More by Dispositional Framework",
			supportingText:
				"School or district policies, state statutes, and criminal laws that form the basis for conduct absolutes and inform a professional educator’s decision making. ",
		};
	}
	return {
		icon: "checkbox-marked-circle-outline",
		label: text ?? "Framework",
		supportingText: "Select this option",
	};
};

type BinaryChoiceTheme = ReturnType<typeof useAppTheme>;

const getCardColors = ({
	theme,
	gradedColors,
	graded,
	isAnswered,
	isSelectedAnswer,
	isActive,
}: {
	theme: BinaryChoiceTheme;
	gradedColors: { border: string; background: string };
	graded: boolean;
	isAnswered: boolean;
	isSelectedAnswer: boolean;
	isActive: boolean;
}) => {
	if (graded) {
		return {
			borderColor: gradedColors.border,
			backgroundColor: gradedColors.background,
		};
	}
	if (!isAnswered && isSelectedAnswer) {
		return {
			borderColor: theme.colors.primary,
			backgroundColor: theme.colors.primaryContainer,
		};
	}
	return {
		borderColor: isActive ? theme.colors.primary : theme.colors.outlineVariant,
		backgroundColor: theme.colors.surface,
	};
};

const ChoiceCard = ({
	choice,
	isAnswered,
	isSelectedAnswer,
	isCorrectAnswer,
	isActive,
	onSelect,
	theme,
}: {
	choice: Choice;
	isAnswered: boolean;
	isSelectedAnswer: boolean;
	isCorrectAnswer: boolean;
	isActive: boolean;
	onSelect: (id: string) => void;
	theme: BinaryChoiceTheme;
}) => {
	const choiceMeta = getChoiceMeta(choice.text);
	const answerType = isCorrectAnswer ? "correct" : "incorrect";
	const graded = isAnswered && (isSelectedAnswer || isCorrectAnswer);
	const gradedColors = theme.colors.answer[answerType];

	const { borderColor, backgroundColor } = getCardColors({
		theme,
		gradedColors,
		graded,
		isAnswered,
		isSelectedAnswer,
		isActive,
	});

	const accentColor = isAnswered ? gradedColors.border : theme.colors.primary;
	const checkboxIcon = isAnswered && !isCorrectAnswer ? "close" : "check";

	return (
		<Pressable
			key={choice.id}
			onPress={() => onSelect(choice.id)}
			accessibilityRole="radio"
			accessibilityState={{ selected: isSelectedAnswer }}
			accessibilityLabel={choice.text}
			style={{
				flex: 1,
				minHeight: 128,
				padding: 20,
				borderRadius: 0,
				borderWidth: 2,
				borderColor,
				backgroundColor,
				cursor: "pointer",
				opacity: isAnswered && !isSelectedAnswer && !isCorrectAnswer ? 0.82 : 1,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="flex-start"
				justifyContent="space-between"
				gap={16}
			>
				<Box
					width={44}
					height={44}
					alignItems="center"
					justifyContent="center"
					backgroundColor={
						isSelectedAnswer ? accentColor : theme.colors.surfaceVariant
					}
				>
					<Icon
						source={choiceMeta.icon}
						size={24}
						color={
							isSelectedAnswer
								? theme.colors.onPrimary
								: theme.colors.onSurfaceVariant
						}
					/>
				</Box>
				{isSelectedAnswer ? (
					<Box
						width={28}
						height={28}
						alignItems="center"
						justifyContent="center"
						borderWidth={2}
						borderColor={accentColor}
						backgroundColor={accentColor}
					>
						<Icon
							source={checkboxIcon}
							size={18}
							color={theme.colors.onPrimary}
						/>
					</Box>
				) : null}
			</Box>
			<Box gap={6} paddingTop={16}>
				<Text
					variant="titleMedium"
					style={{
						color: graded ? theme.colors.shadow : theme.colors.onSurface,
						fontWeight: "700",
					}}
				>
					{choiceMeta.label}
				</Text>
			</Box>
		</Pressable>
	);
};

const SelectionSummary = ({
	selectedChoice,
	selectedFrameworkLabel,
	theme,
}: {
	selectedChoice?: Choice;
	selectedFrameworkLabel: string | null;
	theme: BinaryChoiceTheme;
}) => {
	const hasSelection = !!selectedChoice;

	return (
		<Box
			flexDirection="row"
			gap={12}
			padding={16}
			borderWidth={1}
			borderColor={
				hasSelection ? theme.colors.primary : theme.colors.outlineVariant
			}
			backgroundColor={
				hasSelection ? theme.colors.primaryContainer : theme.colors.surface
			}
			alignItems="flex-start"
		>
			<View style={{ marginTop: 1 }}>
				<Icon
					source={hasSelection ? "information" : "cursor-default-click"}
					size={22}
					color={
						hasSelection ? theme.colors.primary : theme.colors.onSurfaceVariant
					}
				/>
			</View>
			<Box flex={1} gap={6}>
				<Text
					variant="labelLarge"
					style={{
						color: hasSelection
							? theme.colors.onPrimaryContainer
							: theme.colors.onSurface,
						fontWeight: "700",
					}}
				>
					{selectedFrameworkLabel ?? "Choose a framework"}
				</Text>
				{selectedChoice?.description ? (
					<Text
						variant="bodyMedium"
						style={{ color: theme.colors.onPrimaryContainer }}
					>
						{selectedChoice.description}
					</Text>
				) : null}
			</Box>
		</Box>
	);
};

const BinaryChoice = ({
	choices,
	isAnswered,
	selectedChoiceIds,
	correctAnswerIds = [],
	itemId,
	onSelect,
}: Props) => {
	const theme = useAppTheme();

	const previewId = useSessionStore(
		(state) => state.itemStates?.get(itemId)?.previewChoiceId,
	);
	const setPreviewChoiceId = useSessionStore(
		(state) => state.setPreviewChoiceId,
	);

	const selectedId = selectedChoiceIds[0];
	const activeId = isAnswered ? (previewId ?? selectedId) : selectedId;
	const activeChoice = choices.find((c) => c.id === activeId);
	const activeFrameworkLabel = activeChoice
		? deriveFrameworkLabel(activeChoice.text)
		: null;

	const handlePress = (id: string) => {
		if (isAnswered) {
			setPreviewChoiceId(itemId, id);
		} else {
			onSelect(id);
		}
	};

	return (
		<Box gap={16} paddingTop={16}>
			<Box flexDirection="row" gap={16}>
				{choices.map((choice) => (
					<ChoiceCard
						key={choice.id}
						choice={choice}
						isAnswered={isAnswered}
						isSelectedAnswer={choice.id === selectedId}
						isCorrectAnswer={correctAnswerIds.includes(choice.id)}
						isActive={choice.id === activeId}
						onSelect={handlePress}
						theme={theme}
					/>
				))}
			</Box>

			<SelectionSummary
				selectedChoice={activeChoice}
				selectedFrameworkLabel={activeFrameworkLabel}
				theme={theme}
			/>
		</Box>
	);
};

export default BinaryChoice;
